/* eslint-disable complexity */
/**
 * Initializes device-specific information for logging purposes.
 * This includes OS and browser details. It uses modern APIs when available,
 * and falls back to userAgent parsing when necessary.
 *
 * ‼️ NOTE: This function is intended to be stringified and injected into the HTML.
 * Avoid using external dependencies or imports within this function or extracting functions outside.
 */

declare global {
  interface Window {
    __DEVICE_DETAILS__?: {
      osName?: string;
      osVersion?: string;
      deviceModel?: string;
      browserName?: string;
      browserBrands?: string;
      browserVersion?: string;
    };
  }
  interface Navigator {
    readonly userAgentData?: {
      readonly brands: ReadonlyArray<{
        brand: string;
        version: string;
      }>;
      readonly platform: string;
      getHighEntropyValues?: (hints: string[]) => Promise<{
        platformVersion?: string;
        model?: string;
      }>;
    };
  }
}

export async function initializeDeviceSpecificsForLogging() {
  function parseVersion(ua: string, regex: RegExp) {
    const match = ua.match(regex);
    return match?.[1]?.replace(/_/g, '.');
  }

  let osName: string | undefined;
  let osVersion: string | undefined;
  let browserName: string | undefined;
  let browserVersion: string | undefined;
  let deviceModel: string | undefined;

  const initializeOSInfo = async () => {
    // Modern API (Chromium 89+)
    if (window?.navigator.userAgentData?.platform) {
      try {
        const platform = window?.navigator.userAgentData.platform;

        if (window?.navigator.userAgentData.getHighEntropyValues) {
          const uaData =
            await window?.navigator.userAgentData.getHighEntropyValues([
              'platformVersion',
              'model',
            ]);
          if (uaData.platformVersion) osVersion = uaData.platformVersion;
          if (uaData.model) deviceModel = uaData.model;
        }

        if (/^mac/i.test(platform)) osName = 'macOS';
        else if (/^win/i.test(platform)) osName = 'Windows';
        else if (/^linux/i.test(platform)) osName = 'Linux';
        else if (/^android/i.test(platform)) osName = 'Android';
        else if (/^chrome os/i.test(platform)) osName = 'ChromeOS';
        else if (/^ios/i.test(platform)) osName = 'iOS';

        window.__DEVICE_DETAILS__ = {
          ...window.__DEVICE_DETAILS__,
          osName: osName || 'unknown',
          osVersion: osVersion || 'unknown',
          deviceModel: deviceModel || 'unknown',
        };
        return;
      } catch (err) {
        console.warn('UserAgentData detection failed:', err);
      }
    }

    // Fallback: userAgent and platform
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || 'unknown';

    if (/Windows NT/i.test(ua)) {
      osName = 'Windows';
      osVersion = parseVersion(ua, /Windows NT ([0-9.]+)/i);
    } else if (/Android/i.test(ua)) {
      osName = 'Android';
      osVersion = parseVersion(ua, /Android ([0-9.]+)/i);
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      osName = 'iOS';
      const versionMatch = parseVersion(ua, /OS ([0-9_]+)/i);
      const versionToken = parseVersion(ua, /Version\/([0-9.]+)/i);
      osVersion = versionToken || versionMatch;
    } else if (/Mac OS X/i.test(ua)) {
      osName = 'macOS';
      const versionMatch = parseVersion(ua, /Mac OS X ([0-9_]+)/i);
      const versionToken = parseVersion(ua, /Version\/([0-9.]+)/i);
      osVersion = versionToken || versionMatch;
    } else if (/CrOS/i.test(ua)) {
      osName = 'ChromeOS';
    } else if (/Linux/i.test(platform)) {
      osName = 'Linux';
    }

    window.__DEVICE_DETAILS__ = {
      ...window.__DEVICE_DETAILS__,
      osName: osName || 'unknown',
      osVersion: osVersion || 'unknown',
      deviceModel: deviceModel || 'unknown',
    };
  };
  const initializeBrowserInfo = () => {
    // Modern API path (Chromium 89+)
    if (window?.navigator?.userAgentData?.brands?.length) {
      try {
        const brands = window?.navigator.userAgentData.brands || [];
        const brandEntry = brands.find(b =>
          /Chrome|Chromium|Edge|Opera|Brave/i.test(b.brand),
        );

        browserName = brandEntry ? brandEntry.brand : brands[0]?.brand;
        browserVersion = brandEntry ? brandEntry.version : brands[0]?.version;

        window.__DEVICE_DETAILS__ = {
          ...window.__DEVICE_DETAILS__,
          browserName: browserName || 'unknown',
          browserVersion: browserVersion || 'unknown',
          browserBrands: brands.map(b => b.brand).join(', '),
        };

        return;
      } catch (err) {
        console.warn('UserAgentData detection failed:', err);
      }
    }

    // Fallback: use userAgent string parsing
    const ua = navigator.userAgent;

    // Order matters because of overlapping patterns (Edge, Opera, Chrome, Safari...)
    if (/Edg\//i.test(ua)) {
      browserName = 'Microsoft Edge';
      browserVersion = ua.match(/Edg\/([0-9.]+)/i)?.[1];
    } else if (/OPR\//i.test(ua)) {
      browserName = 'Opera';
      browserVersion = ua.match(/OPR\/([0-9.]+)/i)?.[1];
    } else if (/chrome\//i.test(ua)) {
      browserName = 'Chrome';
      browserVersion = ua.match(/Chrome\/([0-9.]+)/i)?.[1];
    } else if (/safari\//i.test(ua) && /Version\//i.test(ua)) {
      browserName = 'Safari';
      browserVersion = ua.match(/Version\/([0-9.]+)/i)?.[1];
    } else if (/firefox\//i.test(ua)) {
      browserName = 'Firefox';
      browserVersion = ua.match(/Firefox\/([0-9.]+)/i)?.[1];
    } else if (/MSIE\s|Trident\//i.test(ua)) {
      browserName = 'Internet Explorer';
      browserVersion = ua.match(/(MSIE\s|rv:)([0-9.]+)/i)?.[2];
    }

    window.__DEVICE_DETAILS__ = {
      ...window.__DEVICE_DETAILS__,
      browserName: browserName || 'unknown',
      browserVersion: browserVersion || 'unknown',
    };
  };

  try {
    initializeBrowserInfo();
    await initializeOSInfo();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error initializing browser and OS info:', error);
  }
}
